/* Coach Case Detail — Phase 3 of Compliance Monitoring & Support.
 *
 * Renders at /coe/compliance/cases/:id. The page the coach reaches
 * after clicking a row in the queue. Bundles patient summary, the
 * patient↔coach chat thread, internal notes log, and an Escalate-to-
 * doctor action — all the day-to-day tools a coach needs.
 *
 * Backend endpoints used:
 *   GET  /coe/compliance/cases/{id}                      — header summary
 *   GET  /coe/compliance/cases/{id}/thread               — chat + messages
 *   POST /coe/compliance/cases/{id}/thread/messages      — coach posts
 *   GET  /coe/compliance/cases/{id}/notes                — internal notes
 *   POST /coe/compliance/cases/{id}/notes                — add note
 *   GET  /coe/compliance/cases/{id}/escalations          — case escalations
 *   POST /coe/compliance/cases/{id}/escalate             — raise escalation
 *
 * Layout:
 *   Header bar       — patient name + photo, case code, doctor, status badge
 *   Left (8 col)     — chat thread (system message + chronological messages
 *                       + composer input)
 *   Right (4 col)    — Patient details, Internal notes (compose + log),
 *                       Escalations (raise + history)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    ArrowLeft, Heart, Stethoscope, ChatCircleDots, NotePencil, Warning,
    PaperPlaneTilt, Clock, CheckCircle, User, Sparkle, ShieldWarning,
    ClockUser, ChatTeardropDots,
    // iter102.15 → 102.21 — CommandHeaderShell rebuild imports.
    // iter102.21 — swapped Wrench (my invention) → PencilSimple to
    // match the canonical doctor-portal Planning chip icons. Sparkle
    // was already imported above.
    Buildings, Hash, CalendarBlank, Phone, HeartStraight, PencilSimple,
} from "@phosphor-icons/react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
// iter98.99ew — Inline access to Log Wear + Check-in modals so the PES
// can act without bouncing back to the Compliance Queue.
import LogWearForPatientModal from "@/components/coe/LogWearForPatientModal";
import CheckInNowModal from "@/components/coe/CheckInNowModal";
// iter102.15 → 102.23 — Switched header to CommandHeaderShell (same
// grammar as the doctor's Patient header) so the PES sees the identity
// band + chip strip pattern used across the app. Kept `CaseInfoChipRow`
// import out — its chips are now folded into the shell's own Zone B.
// iter102.23 — Use the canonical `statusLabel` + `statusToneClass`
// from `@/lib/caseStatus` so the PES portal reads the exact same
// case-status vocabulary the doctor sees (e.g. `assigned_to_production`
// collapses to "In Production" for both). Killed the hand-rolled
// `titleCase` + `STATUS_TONE` map that was causing portal drift.
import CommandHeaderShell from "@/components/shared/CommandHeaderShell";
import { statusLabel, statusToneClass } from "@/lib/caseStatus";


/* ════════════════════════════════════════════════════════════════════
 * Helpers
 * ════════════════════════════════════════════════════════════════════ */
function relativeIso(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch { return "—"; }
}

function timestampLong(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString([], {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch { return ""; }
}

function initialsOf(name = "") {
    return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
}

function Avatar({ name, image, size = 36 }) {
    const cls = "rounded-full object-cover ring-1 ring-border";
    if (image) return <img src={image} alt={name || ""} className={cls} style={{ width: size, height: size }} />;
    return (
        <div
            className="rounded-full grid place-items-center ring-1 ring-border text-white font-display font-bold"
            style={{
                width: size, height: size, fontSize: Math.max(10, size / 3),
                background: "linear-gradient(135deg, hsl(180 50% 45%), hsl(200 60% 50%))",
            }}
        >
            {initialsOf(name)}
        </div>
    );
}

/* QuickActionCard — iter102.17 → iter102.20
 * Sidebar CTA tile used above the Escalations panel on the Compliance
 * Case Detail page. Renders as a bordered card with a tinted icon disc,
 * title, one-line description, and a full-width bottom-anchored button.
 *
 * iter102.20 palette (per user direction):
 *   • teal   → canonical clinical action (Log Wear).
 *              Uses the app's brand primary — signals "core action".
 *   • amber  → attention-needed workflow (Check in).
 *              Reaching out to a patient who has gone quiet is an
 *              attention/warning-shade action, not a "warm outreach".
 *   • rose   → escalation / raise to human (Escalate to doctor).
 *              Warm-red carries the interpersonal weight of pinging
 *              another human for help.
 *   Washes softened, icon discs sit on a 1px tinted ring instead of a
 *   flat fill for an editorial rhythm across the rail.
 */
function QuickActionCard({ icon: Icon, title, desc, tone = "teal", onClick, testid }) {
    const toneMap = {
        teal: {
            border: "border-primary/15",
            wash:   "from-primary/[0.06] via-primary/[0.02] to-transparent",
            disc:   "bg-primary/10 ring-1 ring-primary/25",
            iconFg: "text-primary",
            over:   "text-primary/80",
            btn:    "bg-primary hover:bg-primary/90 text-white",
        },
        rose: {
            border: "border-rose-100",
            wash:   "from-rose-50/45 via-rose-50/15 to-transparent",
            disc:   "bg-rose-100/80 ring-1 ring-rose-200/60",
            iconFg: "text-rose-700",
            over:   "text-rose-700/80",
            btn:    "bg-rose-600 hover:bg-rose-700 text-white",
        },
        amber: {
            border: "border-amber-100",
            wash:   "from-amber-50/45 via-amber-50/15 to-transparent",
            disc:   "bg-amber-100/80 ring-1 ring-amber-200/60",
            iconFg: "text-amber-700",
            over:   "text-amber-700/80",
            btn:    "bg-amber-600 hover:bg-amber-700 text-white",
        },
    };
    const t = toneMap[tone] || toneMap.teal;
    return (
        <div
            className={`relative rounded-2xl border bg-card overflow-hidden ${t.border}`}
            data-testid={`${testid}-card`}
        >
            <div className={`absolute inset-0 bg-gradient-to-br ${t.wash} pointer-events-none`} aria-hidden />
            <div className="relative px-4 py-3.5 space-y-3">
                <div className="flex items-start gap-2.5">
                    <div className={`w-8 h-8 rounded-lg ${t.disc} grid place-items-center shrink-0`}>
                        <Icon size={14} weight="duotone" className={t.iconFg} />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className={`text-[10px] font-bold uppercase tracking-[0.14em] ${t.over}`}>Quick action</div>
                        <div className="text-[14px] font-semibold text-foreground leading-tight mt-0.5">{title}</div>
                        <p className="text-[11.5px] text-muted-foreground leading-snug mt-1">{desc}</p>
                    </div>
                </div>
                <Button
                    onClick={onClick}
                    className={`w-full rounded-full h-9 text-[13px] font-semibold shadow-sm hover:shadow-md transition-shadow ${t.btn}`}
                    data-testid={testid}
                >
                    <Icon size={13} weight="fill" className="mr-1.5" />
                    {title}
                </Button>
            </div>
        </div>
    );
}

// iter102.23 — Hand-rolled STATUS_TONE dropped in favour of the canonical
// `statusToneClass` from `@/lib/caseStatus`. See lib/caseStatus.js
// STATUS_CONFIG for the single source of truth on tone-per-status.


/* ════════════════════════════════════════════════════════════════════
 * Page
 * ════════════════════════════════════════════════════════════════════ */
export default function ComplianceCaseDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    // iter98.99ew — Inline Log Wear + Check-in modal state.
    const [logWearOpen, setLogWearOpen] = useState(false);
    const [checkInOpen, setCheckInOpen] = useState(false);

    const loadCase = async () => {
        try {
            const r = await api.get(`/coe/compliance/cases/${id}`);
            setCaseData(r.data);
        } catch (e) {
            if (e?.response?.status === 403 || e?.response?.status === 404) {
                toast.error(e?.response?.data?.detail || "This case isn't available to you.");
                navigate("/coe/compliance");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadCase(); }, [id]);

    if (loading) {
        return <div className="p-10 text-center text-sm text-muted-foreground">Loading case…</div>;
    }
    if (!caseData) return null;

    return (
        <div className="space-y-5" data-testid="coe-compliance-case-detail">
            {/* Back link sits above the header, mirroring the doctor's
                Patient page (small ghost link, not chrome-heavy). */}
            <button
                onClick={() => navigate("/coe/compliance")}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition"
                data-testid="coe-cd-back"
            >
                <ArrowLeft size={16} /> Back to compliance queue
            </button>

            {/* ── Command header (iter102.15) ──────────────────────────
                Rebuilt on the shared CommandHeaderShell so this page
                shares the doctor's Patient-header grammar:
                  · Zone A — avatar + identity kicker + name +
                    demographics · phone suffix + assignment mini-stack
                    (Clinic + Doctor) + status pill + action toolbar.
                  · Zone B — 6-cell chip strip with case-scoped facts.
                ──────────────────────────────────────────────────────── */}
            {(() => {
                const fmtDate = (iso) => {
                    if (!iso) return "—";
                    try {
                        return new Date(iso).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                        }).replace(/\s/g, "-");
                    } catch { return "—"; }
                };
                // iter102.23 — Canonical status label + tone. We ask
                // for the "doctor" portal variant because the PES sits
                // beside the doctor in the treatment lifecycle — they
                // don't need to see backstage production sub-states
                // (assigned_to_production / submitted_to_production /
                // in_production all collapse to "In Production").
                const statusVal = statusLabel(caseData.status, "doctor");
                const statusToneCls = statusToneClass(caseData.status) || "bg-muted text-muted-foreground";
                const statusPill = caseData.status ? (
                    <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] ${statusToneCls}`}
                        data-testid="coe-cd-status-pill"
                    >
                        <Sparkle size={9} weight="fill" /> {statusVal}
                    </span>
                ) : null;

                // Contact phone appended as a demographicsSuffix, exactly
                // the way the doctor's PatientCommandHeader stacks it
                // on its own line under the demographics row.
                const contactSuffix = caseData.patient_phone ? (
                    <>
                        <span className="basis-full h-0 w-full" aria-hidden />
                        <span
                            className="inline-flex items-center gap-1 text-[11.5px] text-muted-foreground"
                            data-testid="coe-cd-patient-phone"
                        >
                            <Phone size={11} weight="bold" className="text-muted-foreground/70" />
                            <a
                                href={`tel:${caseData.patient_phone.replace(/\s/g, "")}`}
                                className="hover:text-primary hover:underline tabular-nums"
                                title={`Call ${caseData.patient_name}`}
                            >
                                {caseData.patient_phone}
                            </a>
                        </span>
                    </>
                ) : null;

                const chips = [
                    // iter102.21 — Labels, values, testids and icons all
                    // realigned to the canonical set used by
                    // DoctorCaseCommandHeader / PlannerCaseCommandHeader /
                    // admin CaseHeader / shared CaseInfoChipRow. Zero
                    // portal-to-portal drift now: same case → same chips
                    // → same words → same testids.
                    {
                        icon: Hash,
                        label: "Case ID",
                        value: caseData.code || "—",
                        mono: true,
                        testid: "case-chip-code",
                        title: caseData.code,
                    },
                    {
                        icon: CalendarBlank,
                        label: "Created",
                        value: fmtDate(caseData.created_at),
                        testid: "case-chip-created",
                    },
                    {
                        icon: caseData.planning_mode === "doctor" ? PencilSimple : Sparkle,
                        label: "Planning",
                        value: caseData.planning_mode === "doctor" ? "Self-planned" : "Invisique™ Team",
                        testid: caseData.planning_mode === "doctor" ? "case-planning-mode-self" : "case-planning-mode-coe",
                        title: caseData.planning_mode === "doctor"
                            ? "The doctor authored the treatment plan on this case"
                            : "Invisique™ Planning Team authored the treatment plan",
                    },
                    {
                        // "Treatment start" is PES-specific (compliance
                        // work can't begin until the patient is fitted).
                        // Every other portal-side chip has been aligned
                        // to the canonical set above.
                        icon: HeartStraight,
                        label: "Treatment start",
                        value: fmtDate(caseData.treatment_started_at),
                        tone: caseData.treatment_started_at ? "emerald" : "slate",
                        testid: "case-chip-treatment-start",
                    },
                ];

                return (
                    <CommandHeaderShell
                        testId="coe-compliance-case-header"
                        avatar={<Avatar name={caseData.patient_name} image={caseData.patient_image} size={56} />}
                        identityLabel="Patient · Compliance case"
                        name={caseData.patient_name || "Unnamed patient"}
                        nameTitle={caseData.patient_name}
                        // iter102.19 → 102.21 — Demographics array
                        // was `[code]` which duplicated the Zone-B
                        // "Case ID" chip right below (originally I
                        // labelled it "Case code" — drift fixed in
                        // 102.21 to match every other portal). Emptied
                        // so the identity line renders just the phone
                        // dial-link via demographicsSuffix.
                        demographics={[]}
                        demographicsSuffix={contactSuffix}
                        assignments={[
                            {
                                label: "Primary clinic",
                                value: caseData.doctor_clinic || "—",
                                icon: Buildings,
                                testid: "coe-cd-clinic",
                                title: caseData.doctor_clinic,
                            },
                            {
                                label: "Treating doctor",
                                value: caseData.doctor_name || "—",
                                icon: Stethoscope,
                                testid: "coe-cd-doctor",
                                title: caseData.doctor_name,
                            },
                        ]}
                        statusPill={statusPill}
                        chips={chips}
                    />
                );
            })()}

            {/* ── Body grid ──────────────────────────────────────────
                iter102.16 — Right rail slimmed to just Escalations.
                `PatientDetailsCard` was fully redundant with the
                CommandHeaderShell chip strip introduced in iter102.15,
                so it's been removed. Chat now runs 8 cols wide, the
                Escalations panel keeps its 4-col slot.
                iter102.17 — Log Wear + Check-in moved out of the header
                right-slot into two individual sidebar cards. This
                gives each action room to breathe (icon + one-line
                context + full-width button), and de-clutters the
                header so the identity band stays scannable. */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
                <div className="lg:col-span-8 space-y-5">
                    <ChatPanel caseId={id} />
                </div>
                <div className="lg:col-span-4 space-y-5">
                    {/* Individual quick-action cards (iter102.17). */}
                    <div className="space-y-3" data-testid="coe-cd-quick-actions">
                        <QuickActionCard
                            icon={ClockUser}
                            title="Log Wear"
                            desc="Record today's wear-time on the patient's behalf when they confirm over the phone."
                            tone="teal"
                            onClick={() => setLogWearOpen(true)}
                            testid="coe-cd-log-wear-btn"
                        />
                        <QuickActionCard
                            icon={ChatTeardropDots}
                            title="Check in"
                            desc="Send a warm WhatsApp nudge — templated so it goes out in one tap."
                            tone="amber"
                            onClick={() => setCheckInOpen(true)}
                            testid="coe-cd-check-in-btn"
                        />
                    </div>
                    {/* iter98.99ew — Internal Notes panel removed from the
                        right rail. Coaches use the chat thread + outcome
                        chips for working notes; standalone internal notes
                        rarely got read and added clutter. */}
                    <EscalationsPanel caseId={id} doctorName={caseData.doctor_name} />
                </div>
            </div>

            {/* Inline Log Wear + Check-in modals (iter98.99ew). */}
            <LogWearForPatientModal
                open={logWearOpen}
                caseRow={caseData}
                onClose={() => setLogWearOpen(false)}
                onSaved={loadCase}
            />
            <CheckInNowModal
                open={checkInOpen}
                caseRow={caseData}
                onClose={() => setCheckInOpen(false)}
                onSent={loadCase}
            />
        </div>
    );
}


/* ════════════════════════════════════════════════════════════════════
 * Chat Panel
 * ════════════════════════════════════════════════════════════════════ */
function ChatPanel({ caseId }) {
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const scrollRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get(`/coe/compliance/cases/${caseId}/thread`);
            setThread(r.data?.thread || null);
            setMessages(r.data?.messages || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [caseId]);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            const r = await api.post(`/coe/compliance/cases/${caseId}/thread/messages`, { body });
            setMessages((arr) => [...arr, r.data]);
            setDraft("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send");
        } finally { setSending(false); }
    };

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" style={{ minHeight: 520 }} data-testid="coe-cd-chat">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ChatCircleDots size={15} className="text-primary" />
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Patient chat</span>
                </div>
                {thread && (
                    <span className="text-[11px] text-muted-foreground">
                        Started {timestampLong(thread.created_at)}
                    </span>
                )}
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-secondary/10" style={{ maxHeight: 520 }}>
                {loading ? (
                    <div className="text-center text-sm text-muted-foreground py-8">Loading thread…</div>
                ) : !thread ? (
                    <NotYetActiveEmpty />
                ) : messages.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground py-8">No messages yet.</div>
                ) : (
                    messages.map((m) => <MessageBubble key={m.id} msg={m} />)
                )}
            </div>

            {/* Composer */}
            {thread && (
                <form onSubmit={send} className="border-t border-border p-3 flex items-end gap-2 bg-card">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                        }}
                        placeholder="Message your patient… (Enter to send · Shift+Enter for newline)"
                        rows={2}
                        className="flex-1 resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                        data-testid="coe-cd-chat-input"
                    />
                    <button
                        type="submit"
                        disabled={!draft.trim() || sending}
                        className="px-3 h-10 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-1.5"
                        data-testid="coe-cd-chat-send"
                    >
                        <PaperPlaneTilt size={14} weight="fill" />
                        {sending ? "Sending…" : "Send"}
                    </button>
                </form>
            )}
        </div>
    );
}

function NotYetActiveEmpty() {
    return (
        <div className="text-center py-10 px-6">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 grid place-items-center mb-3">
                <Clock size={22} className="text-amber-700" />
            </div>
            <div className="font-display text-base font-semibold">Chat opens once treatment begins</div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md mx-auto leading-relaxed">
                The patient↔coach thread auto-opens when the provider marks aligners fitted. Until then, you can prep notes and review the case.
            </p>
        </div>
    );
}

function MessageBubble({ msg }) {
    if (msg.sender_role === "system") {
        return (
            <div className="flex justify-center" data-testid={`coe-cd-msg-${msg.id}`}>
                <div className="max-w-md text-center rounded-xl bg-primary/10 border border-primary/20 px-4 py-3">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5">
                        <Sparkle size={10} weight="fill" /> Welcome message
                    </div>
                    <div className="text-sm text-foreground leading-relaxed">{msg.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-2">{timestampLong(msg.created_at)}</div>
                </div>
            </div>
        );
    }
    const isCoach = msg.sender_role === "coach";
    return (
        <div className={`flex ${isCoach ? "justify-end" : "justify-start"}`} data-testid={`coe-cd-msg-${msg.id}`}>
            <div className={`max-w-[80%] ${isCoach ? "items-end" : "items-start"} flex flex-col gap-1`}>
                <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isCoach
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-card border border-border rounded-bl-md"
                }`}>
                    {msg.body}
                </div>
                <div className="text-[10px] text-muted-foreground px-1">
                    {isCoach ? (msg.sender_name || "You") : (msg.sender_name || "Patient")} · {timestampLong(msg.created_at)}
                </div>
            </div>
        </div>
    );
}


/* ════════════════════════════════════════════════════════════════════
 * Internal notes
 * ════════════════════════════════════════════════════════════════════ */
function NotesPanel({ caseId }) {
    const [notes, setNotes] = useState([]);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const r = await api.get(`/coe/compliance/cases/${caseId}/notes`);
            setNotes(r.data || []);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, [caseId]);

    const add = async () => {
        const body = draft.trim();
        if (!body || saving) return;
        setSaving(true);
        try {
            const r = await api.post(`/coe/compliance/cases/${caseId}/notes`, { body });
            setNotes((arr) => [r.data, ...arr]);
            setDraft("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save note");
        } finally { setSaving(false); }
    };

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="coe-cd-notes">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <NotePencil size={14} className="text-primary" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Internal notes</span>
            </div>
            <div className="p-4 space-y-3">
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Log a private note — admins + doctor can read it."
                    rows={2}
                    className="w-full resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                    data-testid="coe-cd-note-input"
                />
                <button
                    type="button"
                    onClick={add}
                    disabled={!draft.trim() || saving}
                    className="w-full h-9 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition"
                    data-testid="coe-cd-note-save"
                >
                    {saving ? "Saving…" : "Add note"}
                </button>
                {notes.length === 0 ? (
                    <div className="text-center text-xs text-muted-foreground py-3">No notes yet.</div>
                ) : (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                        {notes.map((n) => (
                            <li key={n.id} className="rounded-lg bg-secondary/40 border border-border px-3 py-2" data-testid={`coe-cd-note-${n.id}`}>
                                <div className="text-sm leading-relaxed">{n.body}</div>
                                <div className="text-[10px] text-muted-foreground mt-1.5">
                                    {n.author_name || "Coach"} · {relativeIso(n.created_at)}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}


/* ════════════════════════════════════════════════════════════════════
 * Escalations
 * ════════════════════════════════════════════════════════════════════ */
function EscalationsPanel({ caseId, doctorName }) {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);

    // iter102.24 — "Dr. Wilson" is warmer + more actionable than
    // "IVQ-CRN-2627-0924". Fall back to a generic label only if the
    // name isn't loaded yet.
    const shortDoctor = (() => {
        if (!doctorName) return "the doctor";
        // Strip an existing "Dr." prefix so we don't get "Dr. Dr. Wilson".
        const stripped = doctorName.replace(/^Dr\.?\s+/i, "").trim();
        // Use the surname if present ("Dr. Wilson"), else the full name.
        const parts = stripped.split(/\s+/);
        const surname = parts.length > 1 ? parts[parts.length - 1] : stripped;
        return `Dr. ${surname}`;
    })();

    const load = async () => {
        try {
            const r = await api.get(`/coe/compliance/cases/${caseId}/escalations`);
            setItems(r.data || []);
        } catch { /* silent */ }
    };
    useEffect(() => { load(); }, [caseId]);

    const raise = async () => {
        const body = draft.trim();
        if (!body || saving) return;
        setSaving(true);
        try {
            const r = await api.post(`/coe/compliance/cases/${caseId}/escalate`, { note: body });
            setItems((arr) => [r.data, ...arr]);
            setDraft("");
            setOpen(false);
            toast.success("Escalation sent to doctor");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not escalate");
        } finally { setSaving(false); }
    };

    const openCount = items.filter((i) => i.status === "open").length;

    return (
        <>
            <div className="rounded-2xl border border-rose-200 bg-rose-50/40 overflow-hidden" data-testid="coe-cd-escalations">
                <div className="px-5 py-3 border-b border-rose-200 bg-rose-100/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ShieldWarning size={14} className="text-rose-700" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-rose-800">Escalations</span>
                        {openCount > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-rose-700 text-white">
                                {openCount} open
                            </span>
                        )}
                    </div>
                </div>
                <div className="p-4 space-y-3">
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="w-full h-10 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition inline-flex items-center justify-center gap-1.5"
                        data-testid="coe-cd-escalate-btn"
                    >
                        <Warning size={14} weight="fill" /> Escalate to doctor
                    </button>
                    {items.length === 0 ? (
                        <div className="text-center text-xs text-muted-foreground py-2">No escalations raised.</div>
                    ) : (
                        <ul className="space-y-2 max-h-72 overflow-y-auto">
                            {items.map((e) => <EscalationItem key={e.id} esc={e} />)}
                        </ul>
                    )}
                </div>
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="coe-cd-escalate-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Warning size={18} className="text-rose-600" weight="fill" /> Escalate to doctor
                        </DialogTitle>
                        <DialogDescription>
                            The doctor on this case will see your note in their compliance inbox and respond directly.
                        </DialogDescription>
                    </DialogHeader>
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={5}
                        placeholder="Describe what the doctor needs to know about this patient. Be specific — symptoms, timing, anything unusual."
                        className="w-full resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                        data-testid="coe-cd-escalate-note-input"
                        autoFocus
                    />
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button
                            onClick={raise}
                            disabled={!draft.trim() || saving}
                            className="bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="coe-cd-escalate-submit"
                        >
                            {saving ? "Sending…" : `Send to ${shortDoctor}`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function EscalationItem({ esc }) {
    const resolved = esc.status === "resolved";
    return (
        <li className={`rounded-lg border px-3 py-2.5 ${resolved ? "bg-emerald-50 border-emerald-200" : "bg-white border-rose-200"}`} data-testid={`coe-cd-esc-${esc.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
                <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase rounded ${
                    resolved ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                }`}>
                    {resolved ? <CheckCircle size={9} weight="fill" /> : <Warning size={9} weight="fill" />}
                    <span className="ml-1">{esc.status}</span>
                </span>
                <span className="text-[10px] text-muted-foreground">{relativeIso(esc.created_at)}</span>
            </div>
            <div className="text-sm leading-relaxed text-foreground">{esc.note}</div>
            {resolved && esc.resolved_note && (
                <div className="mt-2 rounded bg-emerald-100 border border-emerald-200 px-2 py-1.5 text-[12px] text-emerald-900">
                    <span className="font-semibold">Doctor:</span> {esc.resolved_note}
                </div>
            )}
        </li>
    );
}
